import { Toggle, Tooltip } from '@assembly-js/design-system';
import { Button, useMediaQuery, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { ChangeEvent } from 'react';
import { makeStyles } from 'tss-react/mui';

import Activator from 'src/legacy/components/Activator';
import { Action, BaseActionsMenu } from 'src/legacy/components/Dropdowns';
import BaseTypography, {
  FontType,
} from 'src/legacy/components/Text/BaseTypography';
import * as Colors from 'src/theme/colors';
import { BlackHeadings, GraySmall } from 'src/theme/colors';
import ColorUtils from 'src/utils/ColorUtils';

/**
 * TODO: set all ModuleSettingCardProps properties types to not optional once
 * sidebar modularity project is removed.
 */
interface ModuleSettingCardV2Props {
  menuActions?: Action[];
}
interface ModuleSettingCardV1Props extends ModuleSettingCardV2Props {
  fieldName?: string;
  checked?: boolean;
  title: string;
  Icon?: React.ComponentType<{ className: string }>;
  description: string;
  learnMoreLink?: string;
  disabled?: boolean;
  tooltip?: string;
  tooltipContent?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltipClassname?: string;
  hideBorder?: boolean;
  invertBooleanValue?: boolean;
  hideIcon?: boolean;
  titleFontType?: FontType;
  descriptionFontType?: FontType;
  noToggleButton?: boolean;
  children?: React.ReactNode;
  useDesignSystemToggle?: boolean;
}

interface StylingProps {
  hideBorder: boolean;
}
const useStyles = makeStyles<StylingProps>()((theme, props) => ({
  root: {
    borderBottom: props.hideBorder
      ? 'unset'
      : `1px solid ${Colors.DividersAndCardBorders}`,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    '&:first-child': {
      paddingTop: 0,
    },
  },
  title: {
    color: BlackHeadings,
  },
  description: {
    color: GraySmall,
  },
  moduleContentRow: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '70%',
  },
  moduleIconWrapper: {
    backgroundColor: ColorUtils.GetColorDarknessShades(
      theme.palette.primary.main,
    ).light,
    borderRadius: '50%',
    height: 40,
    width: 40,
    minHeight: 40,
    minWidth: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moduleIcon: {
    height: 20,
    width: 20,
    '& path': {
      strokeWidth: 2,
    },
  },
}));

const SettingsToggleCard: React.FC<Prettify<ModuleSettingCardV1Props>> = (
  props,
) => {
  const {
    fieldName,
    checked,
    title,
    Icon,
    description,
    learnMoreLink,
    disabled = false,
    tooltip,
    tooltipContent,
    tooltipPosition,
    tooltipClassname,
    hideBorder = false,
    invertBooleanValue = false,
    children,
    hideIcon = false,
    titleFontType = '15Medium',
    descriptionFontType = '13Regular',
    menuActions,
    noToggleButton = false,
    useDesignSystemToggle = false,
  } = props;
  const { classes } = useStyles({ hideBorder });
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { handleBlur, setFieldValue } = useFormikContext();

  const settingsCardRenderer = () => (
    <div className={`flex flex-col w-full ${classes.root}`}>
      <div className="flex justify-between items-center w-full">
        <div className={`mr-11 ${classes.moduleContentRow}`}>
          {!hideIcon && Icon && (
            <div className={`${classes.moduleIconWrapper} mr-5`}>
              <Icon className={classes.moduleIcon} />
            </div>
          )}
          <div className="flex flex-col grow">
            <BaseTypography fontType={titleFontType} className={classes.title}>
              {title}
            </BaseTypography>
            {description && (
              <BaseTypography
                fontType={descriptionFontType}
                className={classes.description}
              >
                {description}
              </BaseTypography>
            )}
            {learnMoreLink && (
              // wrapped in div to prevent the button from being stretched
              <div>
                <Button color="primary" href={learnMoreLink} target="_blank">
                  Learn more
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 flex justify-end items-center">
          {menuActions && !isMobileScreen && (
            <div className="mx-4">
              <BaseActionsMenu actions={menuActions} />
            </div>
          )}
        </div>
        {!noToggleButton && fieldName && (
          <>
            {useDesignSystemToggle ? (
              <Tooltip
                content={tooltipContent ?? tooltip}
                disabled={!tooltipContent && !tooltip}
                position={tooltipPosition}
                tooltipClassname={tooltipClassname}
              >
                <div>
                  <Toggle
                    name={fieldName}
                    checked={!!(invertBooleanValue ? !checked : checked)}
                    label=""
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const isChecked = e.target.checked;
                      setFieldValue(
                        fieldName,
                        invertBooleanValue ? !isChecked : isChecked,
                      );
                    }}
                    onBlur={handleBlur}
                    disabled={disabled}
                  />
                </div>
              </Tooltip>
            ) : (
              <Activator
                checked={!!(invertBooleanValue ? !checked : checked)}
                label=""
                onChange={(_, isChecked) => {
                  setFieldValue(
                    fieldName,
                    invertBooleanValue ? !isChecked : isChecked,
                  );
                }}
                onBlur={handleBlur}
                name={fieldName}
                disabled={disabled}
                tooltip={tooltip}
                isFixedWidth={false}
              />
            )}
          </>
        )}
      </div>
      {children && <div className="w-full pl-9">{children}</div>}
    </div>
  );

  return settingsCardRenderer();
};

export default SettingsToggleCard;
